import React from 'react';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { SuiContainer, SuiButton, SuiTooltipPopover } from '@splashthat/sui';
import { useSelector } from 'react-redux';
import cond from 'lodash/cond';

import { getPreviewEventUrlFromEventDomain } from 'utils/resolveUrl';
import { baseSpacer } from 'common/styles/variables';
import Workspace from 'common/components/Workspace';
import EventPreviewButton from 'common/components/EventPreviewButton';
import Spacer from 'common/components/Spacer';
import BackButton from 'common/compounds/BackButton';
import { DISABLED_MESSAGE } from 'utils/PermissionCheckWrapper';
import {
    getFormLibraryManager,
    getFormLibraryRestrictedAccess,
    getIsEventMultipleRegistrationFormsEnabled,
} from 'common/store/userProperties/selectors';
import {
    getCanUserApplyFormsUserPermission,
    getCanUserCreateLibraryFormsUserPermission,
} from 'common/store/userPermissions/selectors';

import { FormBuilderContext } from './FormBuilder';
import ScaledFormsTopBarDropdown from '../ScaledForms/ScaledFormsTopBarDropdown';
import ScaledFormsAddToLibraryButton from '../ScaledForms/ScaledFormsAddToLibraryButton';
import { getHasAccessToLibraryComponents } from '../../../FormLibrary/selectors';
import { getFormId } from '../../selectors';
import { getIsFormSavedInLibrary } from '../../selectors/formLibrary';

export const WORKSPACE_SUBTITLE_EDIT = 'Editing';
export const WORKSPACE_SUBTITLE_VIEW = 'Viewing';
export const WORKSPACE_FORM_LIBRARY = 'Form Library';

const DISABLED_INFO = `You do not have permission to edit this form. Please contact the organization
 administrator with questions about permissions.`;
const BASE_CLASS_NAME = 'FormBuilderToolbar';

const FormBuilderToolbar = ({
    closeForm,
    saveForm,
    eventDomain,
    isSaving,
    toolbarTitle: propsToolbarTitle,
    closeButtonLabel,
    isFormLibraryWorkspace,
    formName,
}) => {
    const { formId: routeFormId } = useParams();
    const formId = useSelector(getFormId);
    const isFormSavedInLibrary = useSelector((state) => getIsFormSavedInLibrary(state, formId));
    const hasAccessToLibraryComponents = useSelector((state) => getHasAccessToLibraryComponents(state, formId));
    const hasFormLibraryManager = useSelector(getFormLibraryManager);
    const hasRestrictedAccess = useSelector(getFormLibraryRestrictedAccess);
    const isEventMultipleRegistrationFormsEnabled = useSelector(getIsEventMultipleRegistrationFormsEnabled);

    const canApplyFormsWithRBAC = useSelector(getCanUserApplyFormsUserPermission);
    const canCreateFormsWithRBAC = useSelector(getCanUserCreateLibraryFormsUserPermission);

    const canClose = () => !!closeForm;
    const handleClickClose = () => {
        closeForm && closeForm();
    };

    const handleClickSave = () => {
        saveForm && saveForm();
    };

    const routerLink = (
        <Link to={routeFormId ? `/content/form/${routeFormId}/preview` : '/content/form/preview'}>
            <EventPreviewButton className={`${BASE_CLASS_NAME}__preview`} />
        </Link>
    );

    const domLink = (
        <a href={getPreviewEventUrlFromEventDomain(eventDomain, null, true)} target="_blank">
            <EventPreviewButton className={`${BASE_CLASS_NAME}__preview`} />
        </a>
    );

    const saveButton = (disabled = true) => (
        <Workspace.Topbar.SaveButton
            onClick={handleClickSave}
            isSaving={isSaving}
            disabled={disabled}
            data-qa={`${BASE_CLASS_NAME}__button-save`}
        />
    );

    const getToolbarTitle = (consumerToolbarTitle) => {
        if (isFormLibraryWorkspace) {
            return formName;
        } else if (consumerToolbarTitle) {
            return consumerToolbarTitle;
        }
        return propsToolbarTitle;
    };

    const getSubtitle = (viewOnly) => {
        if (isFormLibraryWorkspace) {
            return WORKSPACE_FORM_LIBRARY;
        } else if (viewOnly) {
            return WORKSPACE_SUBTITLE_VIEW;
        }
        return WORKSPACE_SUBTITLE_EDIT;
    };

    const getIfCanSeeApplyDropdown = (isUsingRoleBasedPermissions) =>
        isUsingRoleBasedPermissions ? canApplyFormsWithRBAC : hasFormLibraryManager || hasRestrictedAccess;

    const getIfCanSeeAddToLibraryButton = (isUsingRoleBasedPermissions) =>
        isUsingRoleBasedPermissions ? canCreateFormsWithRBAC : hasFormLibraryManager;

    const getIsDisabledButton = (isUsingRoleBasedPermissions, canUserEditForm) =>
        cond([
            [() => isUsingRoleBasedPermissions && !canUserEditForm, () => true],
            [() => isFormLibraryWorkspace, () => false],
            [() => isFormSavedInLibrary && !hasAccessToLibraryComponents, () => true],
            [() => isFormSavedInLibrary && isUsingRoleBasedPermissions, () => !hasAccessToLibraryComponents],
            [() => isFormSavedInLibrary, () => !hasRestrictedAccess && !hasFormLibraryManager],
            [() => true, () => false],
        ])();

    const getDisabledContent = (isUsingRoleBasedPermissions) =>
        isUsingRoleBasedPermissions ? DISABLED_MESSAGE : DISABLED_INFO;

    return (
        <SuiContainer className={BASE_CLASS_NAME} variation="flex-horizontal">
            <Workspace>
                <FormBuilderContext.Consumer>
                    {({
                        isBackButtonVisible,
                        backPath,
                        isRoutingContext,
                        viewOnly,
                        toolbarTitle,
                        permissions: { canUserEditForm, isUsingRoleBasedPermissions },
                    }) => (
                        <Workspace.Topbar>
                            {isBackButtonVisible && (
                                <BackButton
                                    fallbackPath={
                                        isFormLibraryWorkspace
                                            ? backPath
                                            : isEventMultipleRegistrationFormsEnabled
                                            ? '/forms'
                                            : '/overview'
                                    }
                                />
                            )}
                            <SuiContainer variation="flex-horizontal full-width full-height">
                                {hasAccessToLibraryComponents &&
                                !isFormLibraryWorkspace &&
                                getIfCanSeeApplyDropdown(isUsingRoleBasedPermissions) ? (
                                    <ScaledFormsTopBarDropdown
                                        subtitle={(viewOnly
                                            ? WORKSPACE_SUBTITLE_VIEW
                                            : WORKSPACE_SUBTITLE_EDIT
                                        ).toUpperCase()}
                                        formName={formName}
                                    />
                                ) : (
                                    <Workspace.Topbar.Title
                                        title={getToolbarTitle(toolbarTitle)}
                                        subtitle={getSubtitle(viewOnly)}
                                    />
                                )}

                                {canClose() && (
                                    <SuiContainer variation="flex-group">
                                        <SuiButton
                                            variation="secondary"
                                            size="small"
                                            label={closeButtonLabel}
                                            onClick={handleClickClose}
                                        />
                                    </SuiContainer>
                                )}

                                <SuiContainer variation="flex-horizontal flex-center">
                                    {eventDomain && (isRoutingContext ? routerLink : domLink)}
                                    <Spacer $spacingY={baseSpacer} />
                                    {hasAccessToLibraryComponents &&
                                        !isFormLibraryWorkspace &&
                                        getIfCanSeeAddToLibraryButton(isUsingRoleBasedPermissions) && (
                                            <ScaledFormsAddToLibraryButton />
                                        )}

                                    {getIsDisabledButton(isUsingRoleBasedPermissions, canUserEditForm) ? (
                                        <SuiTooltipPopover
                                            disabled
                                            content={getDisabledContent(isUsingRoleBasedPermissions, canUserEditForm)}
                                        >
                                            {saveButton()}
                                        </SuiTooltipPopover>
                                    ) : (
                                        saveButton(viewOnly)
                                    )}
                                </SuiContainer>
                            </SuiContainer>
                        </Workspace.Topbar>
                    )}
                </FormBuilderContext.Consumer>
            </Workspace>
        </SuiContainer>
    );
};

FormBuilderToolbar.propTypes = {
    toolbarTitle: PropTypes.string.isRequired,
    closeButtonLabel: PropTypes.string.isRequired,
    closeForm: PropTypes.func,
    saveForm: PropTypes.func.isRequired,
    isSaving: PropTypes.bool.isRequired,
    eventDomain: PropTypes.string,
    isFormLibraryWorkspace: PropTypes.bool,
    formName: PropTypes.string,
};

FormBuilderToolbar.defaultProps = {
    closeForm: null,
    goBack: null,
    eventDomain: null,
    isFormLibraryWorkspace: false,
    formName: '',
};

export default FormBuilderToolbar;
